import { useCallback } from 'react';
import { useIsFetching, useQueryClient, Updater, QueryFilters } from '@tanstack/react-query';
import {
    getRepositoryQueryKey,
    ReservationExtent,
    ReservationResult,
    ServiceOrderStates,
    useInvalidateRepositoryQuery,
    useRepositoryQuery,
    useRepositorySuspenseQuery,
} from '@mews-commander/data-access';

// Main extent for reservation module
const RESERVATIONS_EXTENT: ReservationExtent = {
    reservationGroups: true,
    generalNotes: true,
    channelManagerNotes: true,
    specialRequestNotes: true,
    accounts: true,
    companionships: true,
    availabilityBlocks: true,
    rates: true,
    partnerCompanies: true,
    vouchers: true,
    travelAgencies: true,
    origin: true,
    purchaseOrderNumbers: true,
    orderItems: false, // Explicit anchor for query filtering
};
const RESERVATION_ORDER_ITEMS_EXTENT: ReservationExtent = {
    origin: true,
    orderItems: true,
};

export const useGroupReservationsQuery = <T = ReservationResult>(
    groupId: string,
    { select, enabled }: { select?: (data: ReservationResult) => T; enabled?: boolean } = {}
) =>
    useRepositoryQuery(
        'reservations',
        'getAll',
        {
            groupIds: [groupId],
            extent: RESERVATIONS_EXTENT,
        },
        { select, enabled }
    );

export const useGroupReservationsSuspenseQuery = <T = ReservationResult>(
    groupId: string,
    select?: (data: ReservationResult) => T
) =>
    useRepositorySuspenseQuery(
        'reservations',
        'getAll',
        {
            groupIds: [groupId],
            extent: RESERVATIONS_EXTENT,
        },
        { select }
    );

const nonCanceledStates = Object.values(ServiceOrderStates).filter((state) => state !== ServiceOrderStates.Canceled);
export const useReservationGroupOrderItemsQuery = <T = ReservationResult>(
    groupId: string,
    {
        select,
        nonCanceledOnly = true,
        enabled,
    }: { select?: (data: ReservationResult) => T; nonCanceledOnly?: boolean; enabled?: boolean } = {}
) =>
    useRepositoryQuery(
        'reservations',
        'getAll',
        {
            groupIds: [groupId],
            extent: RESERVATION_ORDER_ITEMS_EXTENT,
            states: nonCanceledOnly ? nonCanceledStates : undefined,
        },
        { select, enabled }
    );
export const useUpdateReservationsQuery = (groupId: string) => {
    const client = useQueryClient();
    return useCallback(
        (
            updater: Updater<ReservationResult | undefined, ReservationResult | undefined>,
            filters?: Omit<QueryFilters, 'queryKey'>
        ) => {
            client.setQueriesData(
                {
                    queryKey: getRepositoryQueryKey('reservations', 'getAll', { groupIds: [groupId] }),
                    ...filters,
                },
                updater
            );
        },
        [groupId, client]
    );
};

export const useAreReservationOrderItemsFetching = (groupId: string) =>
    useIsFetching({
        queryKey: getRepositoryQueryKey('reservations', 'getAll', {
            groupIds: [groupId],
            extent: { orderItems: true },
        }),
    }) > 0;

export const useInvalidateReservationGroupQuery = (groupId: string, { invalidateOrderItemsQuery = false } = {}) => {
    const invalidateQuery = useInvalidateRepositoryQuery();
    return useCallback(async () => {
        await invalidateQuery('reservations', 'getAll', {
            groupIds: [groupId],
            // We filter queries based on whether it loads order items. This is useful in RM, as we fetch order items separately.
            extent: { orderItems: invalidateOrderItemsQuery },
        });
    }, [groupId, invalidateQuery, invalidateOrderItemsQuery]);
};
export const useInvalidateReservationGroupAllQuery = (groupId: string) => {
    const invalidateQuery = useInvalidateRepositoryQuery();
    return useCallback(async () => {
        await invalidateQuery('reservations', 'getAll', {
            groupIds: [groupId],
        });
    }, [groupId, invalidateQuery]);
};
